import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, Play, Image, Eye, Heart, Crown, Lock, X,
  Sparkles, Shirt, Scissors, Palette, Syringe, Camera,
  MapPin, Phone, ExternalLink, Building2, Star, Mail, User, Send, CheckCircle, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { FooterSection } from "../components/landing/FooterSection";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryInfo = {
  fashion: { 
    title: "패션", 
    icon: Shirt, 
    color: "burgundy",
    description: "당신만의 스타일을 찾아드립니다"
  },
  hair: { 
    title: "헤어", 
    icon: Scissors, 
    color: "gold",
    description: "얼굴형에 맞는 헤어스타일 제안"
  },
  makeup: { 
    title: "메이크업", 
    icon: Palette, 
    color: "rose",
    description: "자연스러운 아름다움을 완성하세요"
  },
  procedure: { 
    title: "시술&성형", 
    icon: Syringe, 
    color: "purple",
    description: "전문적인 조언과 정보 제공"
  },
  profile: { 
    title: "프로필 사진", 
    icon: Camera, 
    color: "teal",
    description: "매력적인 프로필 사진 촬영 가이드"
  },
};

export default function ImageConsultingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  
  const [contents, setContents] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "all");

  // URL 파라미터가 바뀌면 카테고리 자동 업데이트
  useEffect(() => {
    const cat = searchParams.get("category");
    if (cat) setSelectedCategory(cat);
  }, [searchParams]);
  const [selectedContent, setSelectedContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAdInquiryModal, setShowAdInquiryModal] = useState(false);
  const [adInquirySubmitting, setAdInquirySubmitting] = useState(false);
  const [adInquirySuccess, setAdInquirySuccess] = useState(false);
  const [adInquiryForm, setAdInquiryForm] = useState({
    company_name: "",
    category: "fashion",
    contact_name: "",
    phone: "",
    email: "",
    message: "",
  });

  useEffect(() => {
    fetchContents();
    fetchSponsors();
  }, [selectedCategory]);

  const fetchContents = async () => {
    setIsLoading(true);
    try {
      const url = selectedCategory === "all" 
        ? `${API_URL}/api/image-consulting/contents`
        : `${API_URL}/api/image-consulting/contents?category=${selectedCategory}`;
      
      const response = await fetch(url);
      const data = await response.json();
      setContents(data.contents || []);
    } catch (error) {
      console.error("Error fetching contents:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSponsors = async () => {
    try {
      const url = selectedCategory === "all" 
        ? `${API_URL}/api/image-consulting/sponsors`
        : `${API_URL}/api/image-consulting/sponsors?category=${selectedCategory}`;
      
      const response = await fetch(url);
      const data = await response.json();
      setSponsors(data.sponsors || []);
    } catch (error) {
      console.error("Error fetching sponsors:", error);
    }
  };

  const handleContentClick = (content) => {
    if (content.is_premium && (!user || user.membership_type === "basic")) {
      setShowUpgradeModal(true);
      return;
    }
    setSelectedContent(content);
  };

  const handleAdInquirySubmit = async (e) => {
    e.preventDefault();
    setAdInquirySubmitting(true);

    try {
      // 실제 API 연동 시 사용
      // const response = await fetch(`${API_URL}/api/ad-inquiry`, {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify(adInquiryForm),
      // });
      
      // 임시: 2초 후 성공 처리
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setAdInquirySuccess(true);
      setAdInquiryForm({
        company_name: "",
        category: "fashion",
        contact_name: "",
        phone: "",
        email: "",
        message: "",
      });
      
      // 3초 후 모달 닫기
      setTimeout(() => {
        setShowAdInquiryModal(false);
        setAdInquirySuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      alert("문의 접수 중 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setAdInquirySubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-stone-200" data-testid="image-consulting-page">
      {/* Hero Section with Background Image */}
      <section className="pt-24 pb-12 bg-stone-200">
        <div className="max-w-[95%] mx-auto">
          <div className="relative rounded-3xl overflow-hidden shadow-lg min-h-[400px] md:min-h-[450px]">
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url('https://customer-assets.emergentagent.com/job_3676335c-89e0-49b4-b27a-8437d63cdcc5/artifacts/syms4awx_ChatGPT%20Image%20Mar%2026%2C%202026%2C%2010_04_56%20AM.png')`
              }}
            />
            
            {/* Overlay gradient for text readability */}
            <div className="absolute inset-0 bg-gradient-to-r from-stone-900/70 via-stone-900/50 to-transparent" />
            
            {/* Content */}
            <div className="relative z-10 p-8 md:p-12 flex flex-col justify-center h-full min-h-[400px] md:min-h-[450px]">
              <button
                onClick={() => navigate("/")}
                className="flex items-center gap-2 text-gold-300 hover:text-gold-200 mb-6 transition-colors w-fit"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>홈으로 돌아가기</span>
              </button>

              <div className="max-w-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gold-500/30 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-gold-400" />
                  </div>
                  <span className="text-gold-400 text-sm font-medium tracking-wider uppercase">
                    Image Consulting
                  </span>
                </div>

                <h1 className="font-sans text-4xl md:text-5xl lg:text-6xl font-[720] text-white mb-6">
                  이미지 컨설팅
                </h1>
                
                <p className="text-white/90 text-lg md:text-xl leading-relaxed">
                  전문 컨설턴트의 맞춤형 조언으로
                  <br className="hidden md:block" />
                  당신만의 매력을 발견하세요.
                </p>
                <p className="text-white/70 text-base md:text-lg mt-3">
                  패션, 헤어, 메이크업, 시술 정보까지
                  <br className="hidden md:block" />
                  종합적인 이미지 컨설팅을 제공합니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="py-8 bg-stone-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => setSelectedCategory("all")}
              className={`px-6 py-3 rounded-full font-medium transition-all ${
                selectedCategory === "all"
                  ? "bg-burgundy-500 text-white shadow-lg"
                  : "bg-stone-100 text-stone-600 hover:bg-stone-200"
              }`}
            >
              전체
            </button>
            {Object.entries(categoryInfo).map(([key, info]) => (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`px-6 py-3 rounded-full font-medium transition-all flex items-center gap-2 ${
                  selectedCategory === key
                    ? "bg-burgundy-500 text-white shadow-lg"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                <info.icon className="w-4 h-4" />
                {info.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content Grid */}
      <section className="py-8 pb-16 bg-stone-200">
        <div className="max-w-6xl mx-auto px-6">
          {isLoading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-4 border-burgundy-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : contents.length === 0 ? (
            <div className="text-center py-20">
              <Image className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500 text-lg">아직 등록된 콘텐츠가 없습니다.</p>
              <p className="text-stone-400 text-sm mt-2">곧 멋진 콘텐츠가 업로드될 예정입니다!</p>
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {contents.map((content) => {
                const category = categoryInfo[content.category] || categoryInfo.fashion;
                const isPremiumLocked = content.is_premium && (!user || user.membership_type === "basic");
                
                return (
                  <motion.div
                    key={content.id}
                    variants={itemVariants}
                    onClick={() => handleContentClick(content)}
                    className={`group bg-stone-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all cursor-pointer ${
                      isPremiumLocked ? "opacity-90" : ""
                    }`}
                  >
                    {/* Thumbnail */}
                    <div className="relative aspect-video bg-stone-200 overflow-hidden">
                      {content.thumbnail_url ? (
                        <img
                          src={content.thumbnail_url.startsWith("http") ? content.thumbnail_url : `${API_URL}${content.thumbnail_url}`}
                          alt={content.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <category.icon className="w-12 h-12 text-stone-300" />
                        </div>
                      )}
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      
                      {/* Content type badge */}
                      <div className="absolute top-3 left-3">
                        {content.content_type === "video" ? (
                          <span className="px-3 py-1 bg-burgundy-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                            <Play className="w-3 h-3" fill="white" />
                            영상
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-gold-500 text-stone-900 text-xs font-medium rounded-full flex items-center gap-1">
                            <Image className="w-3 h-3" />
                            이미지
                          </span>
                        )}
                      </div>

                      {/* Premium badge */}
                      {content.is_premium && (
                        <div className="absolute top-3 right-3">
                          <span className="px-3 py-1 bg-gradient-to-r from-gold-500 to-gold-600 text-stone-900 text-xs font-bold rounded-full flex items-center gap-1">
                            <Crown className="w-3 h-3" />
                            프리미엄
                          </span>
                        </div>
                      )}

                      {/* Lock overlay for premium content */}
                      {isPremiumLocked && (
                        <div className="absolute inset-0 bg-stone-900/50 flex items-center justify-center">
                          <div className="text-center text-white">
                            <Lock className="w-8 h-8 mx-auto mb-2" />
                            <p className="text-sm">정회원 전용</p>
                          </div>
                        </div>
                      )}

                      {/* Category & Stats */}
                      <div className="absolute bottom-3 left-3 right-3 flex justify-between items-center">
                        <span className="text-white text-sm font-medium">
                          {category.title}
                        </span>
                        <div className="flex items-center gap-3 text-white/80 text-xs">
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {content.views || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Heart className="w-3 h-3" />
                            {content.likes || 0}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Content Info */}
                    <div className="p-4">
                      <h3 className="font-bold text-stone-800 text-lg mb-2 line-clamp-1 group-hover:text-burgundy-600 transition-colors">
                        {content.title}
                      </h3>
                      <p className="text-stone-500 text-sm line-clamp-2">
                        {content.description}
                      </p>
                      
                      {/* Tags */}
                      {content.tags && content.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {content.tags.slice(0, 3).map((tag, i) => (
                            <span
                              key={i}
                              className="px-2 py-1 bg-stone-200 text-stone-600 text-xs rounded-full"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </div>
      </section>

      {/* Sponsors/Recommended Partners Section */}
      {sponsors.length > 0 && (
        <section className="py-12 bg-stone-100">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Building2 className="w-5 h-5 text-rose-400" />
                <span className="text-rose-400 font-medium text-sm tracking-wider uppercase">
                  Recommended Partners
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-sans font-[720] text-stone-800">
                {selectedCategory === "all" ? "추천 업체" : `${categoryInfo[selectedCategory]?.title || ""} 추천 업체`}
              </h2>
              <p className="text-stone-500 mt-2">
                New Coupling이 엄선한 믿을 수 있는 파트너입니다
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sponsors.map((sponsor, index) => (
                <motion.div
                  key={sponsor.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all group border border-stone-200"
                >
                  {/* Sponsor Image */}
                  <div className="relative h-48 bg-gradient-to-br from-stone-300 to-rose-200 overflow-hidden">
                    {sponsor.image_url ? (
                      <img
                        src={sponsor.image_url.startsWith("http") ? sponsor.image_url : `${API_URL}${sponsor.image_url}`}
                        alt={sponsor.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Building2 className="w-16 h-16 text-white/30" />
                      </div>
                    )}
                    
                    {/* Category Badge */}
                    <div className="absolute top-3 left-3">
                      <span className="px-3 py-1 bg-rose-400 text-white text-xs font-bold rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3" />
                        추천
                      </span>
                    </div>

                    {/* Overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    
                    {/* Title on image */}
                    <div className="absolute bottom-3 left-3 right-3">
                      <h3 className="text-white font-bold text-lg">{sponsor.title}</h3>
                      {sponsor.location && (
                        <p className="text-white/80 text-sm flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {sponsor.location}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Sponsor Info */}
                  <div className="p-4">
                    <p className="text-stone-600 text-sm line-clamp-2 mb-4">
                      {sponsor.description}
                    </p>

                    <div className="flex items-center justify-between">
                      {sponsor.contact && (
                        <a 
                          href={`tel:${sponsor.contact}`}
                          className="flex items-center gap-1 text-stone-500 hover:text-rose-500 text-sm transition-colors"
                        >
                          <Phone className="w-4 h-4" />
                          {sponsor.contact}
                        </a>
                      )}
                      
                      {sponsor.link_url && (
                        <a
                          href={sponsor.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-4 py-2 bg-rose-400 hover:bg-rose-500 text-white text-sm font-medium rounded-full transition-colors"
                        >
                          방문하기
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Ad Banner Placeholder - matching hero image tone */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 bg-gradient-to-br from-stone-100 via-stone-200 to-rose-100/50 rounded-2xl p-6 md:p-8 text-center border border-stone-300"
            >
              <p className="text-rose-400 text-sm font-medium mb-2">광고 문의</p>
              <h3 className="text-stone-700 text-xl font-bold mb-2">
                이곳에 업체를 홍보하세요
              </h3>
              <p className="text-stone-500 text-sm mb-4">
                New Coupling 회원들에게 직접 다가갈 수 있는 기회입니다
              </p>
              <Button
                onClick={() => setShowAdInquiryModal(true)}
                variant="outline"
                className="border-rose-400 text-rose-500 hover:bg-rose-400 hover:text-white"
              >
                광고 문의하기
              </Button>
            </motion.div>
          </div>
        </section>
      )}

      {/* If no sponsors, show ad banner only */}
      {sponsors.length === 0 && selectedCategory !== "all" && (
        <section className="py-12 bg-stone-100">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-stone-100 via-stone-200 to-rose-100/50 rounded-2xl p-8 text-center border border-stone-300"
            >
              <Building2 className="w-12 h-12 text-rose-400 mx-auto mb-4" />
              <h3 className="text-stone-700 text-2xl font-bold mb-2">
                {categoryInfo[selectedCategory]?.title} 파트너를 모집합니다
              </h3>
              <p className="text-stone-500 mb-6">
                New Coupling 회원들에게 업체를 홍보하고 새로운 고객을 만나보세요
              </p>
              <Button
                onClick={() => setShowAdInquiryModal(true)}
                className="bg-rose-400 hover:bg-rose-500 text-white font-bold"
              >
                파트너 신청하기
              </Button>
            </motion.div>
          </div>
        </section>
      )}

      <FooterSection />

      {/* Content Detail Modal */}
      <AnimatePresence>
        {selectedContent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/80 backdrop-blur-sm p-4 sm:p-6"
            onClick={() => setSelectedContent(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 28 }}
              className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl relative flex flex-col lg:flex-row"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button (floating) */}
              <button
                onClick={() => setSelectedContent(null)}
                className="absolute top-4 right-4 z-20 p-2 bg-stone-900/70 hover:bg-stone-900 rounded-full text-white transition-colors shadow-lg backdrop-blur-sm"
                data-testid="close-modal-btn"
                aria-label="닫기"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Media Section - half on desktop, capped on mobile */}
              <div className="relative bg-gradient-to-br from-stone-100 to-stone-200 lg:w-1/2 flex-shrink-0 h-56 sm:h-72 lg:h-auto lg:max-h-[90vh]">
                {selectedContent.content_type === "video" && selectedContent.media_url ? (
                  <video
                    src={selectedContent.media_url.startsWith("http") ? selectedContent.media_url : `${API_URL}${selectedContent.media_url}`}
                    controls
                    className="w-full h-full object-cover lg:object-contain bg-black"
                  />
                ) : selectedContent.media_url ? (
                  <img
                    src={selectedContent.media_url.startsWith("http") ? selectedContent.media_url : `${API_URL}${selectedContent.media_url}`}
                    alt={selectedContent.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-16 h-16 text-stone-300" />
                  </div>
                )}
                {/* Premium badge */}
                {selectedContent.is_premium && (
                  <div className="absolute top-4 left-4 bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-md">
                    <Crown className="w-3 h-3" />
                    Premium
                  </div>
                )}
              </div>

              {/* Content Info Section - full scroll on its own */}
              <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
                <div className="px-6 lg:px-8 py-6 overflow-y-auto flex-1" data-testid="modal-content-body">
                  {/* Category + meta */}
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 bg-burgundy-50 text-burgundy-600 text-xs font-semibold px-3 py-1.5 rounded-full">
                      {categoryInfo[selectedContent.category]?.title || selectedContent.category}
                    </span>
                    <div className="flex items-center gap-3 text-stone-500 text-xs">
                      <span className="flex items-center gap-1">
                        <Eye className="w-3.5 h-3.5" />
                        {selectedContent.views || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart className="w-3.5 h-3.5" />
                        {selectedContent.likes || 0}
                      </span>
                    </div>
                  </div>

                  {/* Title */}
                  <h2 className="text-xl sm:text-2xl lg:text-[26px] font-[720] text-stone-800 leading-tight mb-3" data-testid="modal-title">
                    {selectedContent.title}
                  </h2>

                  {/* Divider */}
                  <div className="h-px bg-gradient-to-r from-burgundy-200 via-burgundy-100 to-transparent mb-5" />

                  {/* Description (full body content) */}
                  <div className="prose prose-stone prose-sm max-w-none">
                    <p className="text-stone-700 leading-[1.85] whitespace-pre-wrap text-[15px]" data-testid="modal-description">
                      {selectedContent.description}
                    </p>
                  </div>

                  {/* Tags */}
                  {selectedContent.tags && selectedContent.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-6 pt-4 border-t border-stone-100">
                      {selectedContent.tags.map((tag, i) => (
                        <span
                          key={i}
                          className="px-2.5 py-1 bg-stone-50 text-stone-600 text-xs rounded-full border border-stone-200 hover:bg-stone-100 transition-colors"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Sticky footer action */}
                <div className="border-t border-stone-100 px-6 lg:px-8 py-3 bg-stone-50/50 flex items-center justify-between flex-shrink-0">
                  <span className="text-xs text-stone-400">
                    {selectedContent.created_at ? new Date(selectedContent.created_at).toLocaleDateString("ko-KR") : ""}
                  </span>
                  <button
                    onClick={() => setSelectedContent(null)}
                    className="text-xs font-medium text-stone-600 hover:text-burgundy-500 px-4 py-1.5 rounded-lg hover:bg-white transition-colors"
                  >
                    닫기
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upgrade Modal */}
      <AnimatePresence>
        {showUpgradeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/80 backdrop-blur-sm p-4"
            onClick={() => setShowUpgradeModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-3xl max-w-md w-full p-8 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-16 h-16 bg-gradient-to-br from-gold-400 to-gold-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-stone-800 mb-2">
                프리미엄 콘텐츠
              </h3>
              <p className="text-stone-500 mb-6">
                이 콘텐츠는 정회원 이상만 이용 가능합니다.
                <br />
                지금 업그레이드하고 모든 콘텐츠를 즐겨보세요!
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowUpgradeModal(false)}
                  className="flex-1 py-3"
                >
                  취소
                </Button>
                <Button
                  onClick={() => navigate("/membership")}
                  className="flex-1 py-3 bg-burgundy-500 hover:bg-burgundy-600"
                >
                  멤버십 보기
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ad Inquiry Modal */}
      <AnimatePresence>
        {showAdInquiryModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/70 backdrop-blur-sm p-4 overflow-y-auto"
            onClick={() => !adInquirySubmitting && setShowAdInquiryModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl my-8"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="relative bg-gradient-to-br from-stone-100 via-stone-200 to-rose-100/50 p-6 text-center">
                <button
                  onClick={() => !adInquirySubmitting && setShowAdInquiryModal(false)}
                  className="absolute top-4 right-4 p-2 hover:bg-white/50 rounded-full transition-colors text-stone-500 hover:text-stone-700"
                >
                  <X className="w-5 h-5" />
                </button>
                
                <div className="w-16 h-16 bg-rose-400 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Building2 className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-stone-800">광고 문의</h3>
                <p className="text-stone-500 text-sm mt-1">New Coupling에 업체를 홍보하세요</p>
              </div>

              {/* Success State */}
              {adInquirySuccess ? (
                <div className="p-8 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", duration: 0.5 }}
                    className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
                  >
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </motion.div>
                  <h4 className="text-xl font-bold text-stone-800 mb-2">
                    문의가 접수되었습니다!
                  </h4>
                  <p className="text-stone-500">
                    담당자가 검토 후 빠른 시일 내에 연락드리겠습니다.
                  </p>
                </div>
              ) : (
                /* Form */
                <form onSubmit={handleAdInquirySubmit} className="p-6 space-y-4">
                  {/* Company Name */}
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">
                      업체명 *
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                      <input
                        type="text"
                        required
                        value={adInquiryForm.company_name}
                        onChange={(e) => setAdInquiryForm(prev => ({ ...prev, company_name: e.target.value }))}
                        className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-rose-400 focus:border-transparent transition-all"
                        placeholder="업체명을 입력하세요"
                      />
                    </div>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">
                      광고 카테고리 *
                    </label>
                    <select
                      value={adInquiryForm.category}
                      onChange={(e) => setAdInquiryForm(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-rose-400 focus:border-transparent transition-all"
                    >
                      {Object.entries(categoryInfo).map(([key, info]) => (
                        <option key={key} value={key}>{info.title}</option>
                      ))}
                    </select>
                  </div>

                  {/* Contact Name */}
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">
                      담당자명 *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                      <input
                        type="text"
                        required
                        value={adInquiryForm.contact_name}
                        onChange={(e) => setAdInquiryForm(prev => ({ ...prev, contact_name: e.target.value }))}
                        className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-rose-400 focus:border-transparent transition-all"
                        placeholder="담당자 이름"
                      />
                    </div>
                  </div>

                  {/* Phone & Email Row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1.5">
                        연락처 *
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                        <input
                          type="tel"
                          required
                          value={adInquiryForm.phone}
                          onChange={(e) => setAdInquiryForm(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-rose-400 focus:border-transparent transition-all"
                          placeholder="010-0000-0000"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1.5">
                        이메일 *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                        <input
                          type="email"
                          required
                          value={adInquiryForm.email}
                          onChange={(e) => setAdInquiryForm(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-rose-400 focus:border-transparent transition-all"
                          placeholder="email@example.com"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">
                      문의 내용
                    </label>
                    <textarea
                      rows={3}
                      value={adInquiryForm.message}
                      onChange={(e) => setAdInquiryForm(prev => ({ ...prev, message: e.target.value }))}
                      className="w-full px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-rose-400 focus:border-transparent transition-all resize-none"
                      placeholder="광고 관련 문의사항을 남겨주세요"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={adInquirySubmitting}
                    className="w-full py-4 bg-rose-400 hover:bg-rose-500 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                  >
                    {adInquirySubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        접수 중...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        문의 접수하기
                      </>
                    )}
                  </Button>

                  <p className="text-center text-xs text-stone-400 mt-2">
                    * 접수 후 1-2 영업일 내 담당자가 연락드립니다
                  </p>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
